import { EVENTS } from '@archetype-themes/utils/events';

class ShippingCalculator extends HTMLElement {
  cartKey = 'uxflow_cart';
  abortController = new AbortController();
  states = [
    {
      name: "New South Wales",
      abbr: "NSW",
      ranges: [[1000, 1999], [2000, 2599], [2619, 2899], [2921, 2999]]
    },
    {
      name: "Australian Capital Territory",
      abbr: "ACT",
      ranges: [[200, 299], [2600, 2618], [2900, 2920]]
    },
    {
      name: "Victoria",
      abbr: "VIC",
      ranges: [[3000, 3996], [8000, 8999]]
    },
    {
      name: "Queensland",
      abbr: "QLD",
      ranges: [[4000, 4999], [9000, 9999]]
    },
    {
      name: "South Australia",
      abbr: "SA",
      ranges: [[5000, 5799], [5800, 5999]]
    },
    {
      name: "Western Australia",
      abbr: "WA",
      ranges: [[6000, 6797], [6800, 6999]]
    },
    {
      name: "Tasmania",
      abbr: "TAS",
      ranges: [[7000, 7799], [7800, 7999]]
    },
    {
      name: "Northern Territory",
      abbr: "NT",
      ranges: [[800, 899], [900, 999]]
    }
  ]


  constructor() {
    super();
    this.form = this.querySelector('form');
    this.variantPicker = document.querySelector('block-variant-picker');
    this.ratesContainer = this.querySelector('.shipping-rates');
  }

  connectedCallback() {
    this.form.addEventListener('submit', this.onSubmit);

    document.addEventListener(
      EVENTS.cartUpdated,
      (evt) => this.setCart(evt.detail.cart),
      { signal: this.abortController.signal }
    );

    if (this.variantPicker) {
      this.variantPicker.addEventListener(
        `${EVENTS.variantChange}:${this.variantPicker.dataset.sectionId}:${this.variantPicker.dataset.productId}`,
        (evt) => {
          const variant = evt.detail?.variant;
          if (variant?.id) this.dataset.variantId = variant.id;
        },
        { signal: this.abortController.signal }
      );
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.form.removeEventListener('submit', this.onSubmit);
  }

  onStateChange = (e) => {
    const button = this.form.querySelector('button[type="submit"]');
    const value = e.target.value;
    if (value === 'TAS' || value === 'Tasmania') {
      this.renderError(`Shipping upon request. Please <a href="/pages/contact-us" class="text-link">contact us</a> for a quote`);
      if (button) {
        button.disabled = true;
        button.classList.add('disabled')
      }
    } else {
      this.ratesContainer.innerHTML = '';
      if (button) {
        button.disabled = false;
        button.classList.remove('disabled')
      }
    }
  };

  getProvince(postcode) {
    const state = this.states.find(({ ranges }) =>
      ranges.some(([min, max]) => postcode >= min && postcode <= max)
    );
    return state ? state.abbr : null;
  }

  onSubmit = (e) => {
    e.preventDefault();

    // Prevent submit if TAS is selected
    console.log(this.form, this.form.querySelector('#shipping-postcode'));

    const postcode = this.form.querySelector('#shipping-postcode')?.value || null;
    if (!postcode) {
      this.renderError('Incorrect or missing postcode');
    }

    this.province = this.getProvince(postcode);

    if (this.province === 'TAS') {
      this.renderError('Shipping upon request. Please <a href="/pages/contact-us" class="text-link">contact us</a> for a quote');
      return;
    }

    this.handleCartAndShipping();
  };

  async getCurrentCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  getCart() {
    return JSON.parse(localStorage.getItem(this.cartKey) || '{}');
  }

  setCart(cart) {
    localStorage.setItem(this.cartKey, JSON.stringify(cart));
  }

  async clearCart() {
    await fetch('/cart/clear.js', { method: 'POST' });
  }

  async restoreCart() {
    const cart = this.getCart();
    if (!cart?.items?.length) return;
    const items = cart.items.map(({ variant_id, quantity }) => ({ id: variant_id, quantity }));
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
    } catch (error) {
      this.renderError('Error restoring previous cart. Please try again');
      console.log("Error restoring previous cart:", { error });
    }
  }

  async addProductToCart(variantId) {
    try {
      const req = await fetch(`/cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!req.ok) {
        const res = await req.json();
        this.renderError(res.description || 'Error adding product to cart. Please try again');
        console.error('Error adding product to cart:', res);
      }
    } catch (error) {
      this.renderError('Error adding product to cart. Please try again');
      console.error('Error adding product to cart:', error);
      await this.restoreCart();
    }
  }

  async handleCartAndShipping() {
    const button = this.form.querySelector('button[type="submit"]');
    const includeCart = this.form.querySelector('input[type="checkbox"]')?.checked;
    const variantId = this.dataset.variantId;
    const currentCart = await this.getCurrentCart();
    const cartHasItems = currentCart.item_count > 0 || currentCart.items.length > 0;

    if (cartHasItems) this.setCart(currentCart);

    this.ratesContainer.innerHTML = 'Calculating your shipping cost...';
    button.classList.add('btn--loading');

    if (includeCart) {
      await this.processCartFlow([() => this.addProductToCart(variantId)]);
    } else {
      await this.processCartFlow([
        () => this.clearCart(),
        () => this.addProductToCart(variantId)
      ]);
    }

    button.classList.remove('btn--loading');
  }

  async processCartFlow(steps) {
    for (const step of steps) await step();
    await this.calculateShipping();
    await this.clearCart();
    await this.restoreCart();
  }

  async calculateShipping() {
    const formData = new FormData(this.form);
    formData.append('shipping_address[province]', this.province);
    formData.delete('include_current_cart');
    const queryString = new URLSearchParams(formData).toString();

    try {
      const req = await fetch(`/cart/shipping_rates.json?${queryString}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!req.ok) throw new Error('Network response was not ok');
      const data = await req.json();
      this.renderShippingRates(data.shipping_rates);
    } catch (error) {
      this.renderError('Error calculating shipping rates. Please try again');
      console.error('Error preparing shipping:', error);
    }
  }

  renderShippingRates(shippingRates) {
    this.ratesContainer.innerHTML = '';
    if (!shippingRates?.length) {
      this.ratesContainer.innerHTML = '<p>No shipping rates available. <a href="mailto:info@rusticcharminteriors.com" class="text-link" style="color: currentColor">Email us</a>.</p>';
      return;
    }
    shippingRates.forEach((rate) => {
      const rateElement = document.createElement('div');
      rateElement.className = 'shipping-rate';
      rateElement.textContent = `${rate.name}: $${rate.price}`;
      this.ratesContainer.appendChild(rateElement);
    });
  }

  renderError(message) {
    this.ratesContainer.innerHTML = `<div class="shipping-error" style="color:red;">${message}</div>`;
  }
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}